// useLatestConvictionByMarket.ts
// Fetches the latest conviction point per (market, group) for a set of markets
// in a single batch request to GET /api/screener/conviction/latest.
//
// Replaces the previous approach of firing one request per market per group
// against the per-market timeseries endpoint. Note: the batch endpoint reads
// conviction_scores and does not carry open_shares_*; those are filled with 0
// here (unused by the conviction circles). If a consumer needs them, extend the
// endpoint rather than reviving the per-market fan-out.

import { useState, useEffect, useRef } from 'react';
import { apiGet } from './apiClient';
import type { ConvictionTimePoint } from './useConvictionTimeseries';

interface LatestEntry {
    market_id: string;
    group: string;
    ts: string | null;
    raw_implied: number | null;
    direction: string | null;
    n_traders_yes: number;
    n_traders_no: number;
    capital_usd_yes: number;
    capital_usd_no: number;
}

interface LatestResponse {
    groups: string[];
    entries: LatestEntry[];
}

export interface LatestConvictionResult {
    // marketId -> group -> latest point
    byMarket: Map<string, Map<string, ConvictionTimePoint>>;
    // Groups actually present in the response
    activeGroups: string[];
    loading: boolean;
    error: Error | null;
}

function toPoint(e: LatestEntry): ConvictionTimePoint {
    return {
        ts: e.ts ? new Date(e.ts) : new Date(0),
        raw_implied: e.raw_implied ?? 0,
        direction: (e.direction as ConvictionTimePoint['direction']) ?? null,
        n_traders_yes: e.n_traders_yes,
        n_traders_no: e.n_traders_no,
        capital_usd_yes: e.capital_usd_yes,
        capital_usd_no: e.capital_usd_no,
        open_shares_yes: 0,
        open_shares_no: 0,
        net_capital: e.capital_usd_yes - e.capital_usd_no,
        net_traders: e.n_traders_yes - e.n_traders_no,
    };
}

export function useLatestConvictionByMarket(
    marketIds: string[],
    groups: string[]
): LatestConvictionResult {
    const [byMarket, setByMarket] = useState<
        Map<string, Map<string, ConvictionTimePoint>>
    >(new Map());
    const [activeGroups, setActiveGroups] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const abortRef = useRef<AbortController | null>(null);

    const marketsKey = marketIds.join(',');
    const groupsKey = groups.join(',');

    useEffect(() => {
        if (marketIds.length === 0) {
            setByMarket(new Map());
            setActiveGroups([]);
            setLoading(false);
            setError(null);
            return;
        }

        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setLoading(true);
        setError(null);

        const params = new URLSearchParams({ market_ids: marketsKey });
        if (groupsKey) params.set('groups', groupsKey);

        apiGet<LatestResponse>(`/api/screener/conviction/latest?${params}`)
            .then(res => {
                if (ctrl.signal.aborted) return;
                const next = new Map<
                    string,
                    Map<string, ConvictionTimePoint>
                >();
                for (const e of res.entries) {
                    let inner = next.get(e.market_id);
                    if (!inner) {
                        inner = new Map();
                        next.set(e.market_id, inner);
                    }
                    inner.set(e.group, toPoint(e));
                }
                setByMarket(next);
                setActiveGroups(res.groups ?? []);
                setLoading(false);
            })
            .catch((err: Error) => {
                if (ctrl.signal.aborted || err?.name === 'AbortError') return;
                console.error(
                    '[useLatestConvictionByMarket] fetch failed:',
                    err
                );
                setError(err);
                setLoading(false);
            });

        return () => {
            ctrl.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketsKey, groupsKey]);

    return { byMarket, activeGroups, loading, error };
}
